import { useMemo } from 'react';
import * as React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';

import { type BlogPost } from '../../templates/blogPostTemplate';

import { BlogPostHeroMediaVideo } from './BlogPostHeroMediaVideo';

const Wrapper = styled.div<{ $isInterview: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: calc(${({ theme }) => theme.maxWidth.BLOG} + 140px);
  margin: 50px auto -250px;
  padding: 0 20px;

  ${({ $isInterview }) =>
    $isInterview &&
    css`
      width: 440px;
      position: relative;
      max-width: none;
      margin: 0;
      flex: none;
    `}

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    margin: 30px auto -100px;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    width: 100%;
    padding: 0 20px;
  }
`;

const Image = styled.div<{ $isInterview: boolean; $showPhotoOverlay: boolean }>`
  ${({ theme }) => theme.card};
  position: relative;
  width: 100%;
  height: 510px;

  ${({ $showPhotoOverlay, theme }) =>
    $showPhotoOverlay &&
    css`
      ${theme.photoOverlay};
    `}

  ${({ $isInterview }) =>
    $isInterview &&
    css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 390px;

      .corners .right::before {
        border-color: ${({ theme }) => theme.borderColor.MUTED};
      }
    `}

  .gatsby-image-wrapper {
    height: 100%;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    height: auto;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    height: auto;
    width: 100%;
    position: relative;
  }
`;

const Credit = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: rgba(black, 0.9);
  border-radius: $border-radius;
  height: 30px;
  padding: 0 10px;
  color: white;
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  z-index: 1;
`;

interface BlogPostHeaderProps {
  post: BlogPost;
}

export const BlogPostHeroMedia: React.ComponentType<
  BlogPostHeaderProps & { className?: string }
> = React.memo(({ post, className }) => {
  const hasMedia = post.heroLoomVideo || post.heroImage;

  const media = useMemo(() => {
    if (post.heroLoomVideo) {
      return <BlogPostHeroMediaVideo url={post.heroLoomVideo.url} />;
    }

    if (post.heroImage) {
      return (
        <Image
          $isInterview={post.interview}
          $showPhotoOverlay={post.showPhotoOverlay}>
          <span className="corners">
            <span className="left" />
            <span className="right" />
          </span>
          {!post.interview && post.heroImageCredit && (
            <Credit>{post.heroImageCredit}</Credit>
          )}
          <GatsbyImage
            alt={post.heroImage.title}
            image={post.heroImage.gatsbyImageData}
            loading="eager"
            sizes="(min-width: 1010px) 1125px, 100vw"
          />
        </Image>
      );
    }
  }, [
    post.heroImage,
    post.heroImageCredit,
    post.heroLoomVideo,
    post.interview,
    post.showPhotoOverlay
  ]);

  if (!hasMedia) {
    return null;
  }

  return (
    <Wrapper $isInterview={post.interview} className={className}>
      {media}
    </Wrapper>
  );
});
